import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { IVisitor } from '@gettactic/api';
import { api } from '@/lib/api/api';
import { Button, Label, Modal } from '@/components/basic';
import { LoadingSpinner } from '@/components/util/LoadingSpinner';

type Props = {
  setDeleteModal: React.Dispatch<React.SetStateAction<IVisitor | null>>;
  visitor: IVisitor;
};

export function DeleteVisitModal({
  setDeleteModal,
  visitor
}: Props): JSX.Element {
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const submitMutation = useMutation(
    async () => {
      const res = await api.client.stations.deleteStationVisit(
        visitor.visit.id
      );
      return { res };
    },
    {
      onSuccess: () => {
        setDeleteModal(null);
        toast.success('Visit deleted');
      },
      onError: (err) => {
        console.error('Error deleting visit', err);
        toast.error('Failed to delete visit');
      },
      onSettled: async () => {
        setLoading(false);
        await queryClient.invalidateQueries(['stations.stationVisits']);
      }
    }
  );

  return (
    <Modal
      title={`Delete Visitor`}
      opened={true}
      onClose={() => setDeleteModal(null)}
      size="lg"
    >
      <form
        onSubmit={(ev) => {
          ev.preventDefault();
          submitMutation.mutate();
        }}
      >
        <div>
          <Label htmlFor="name" className="mt-4">
            <span>
              Are you sure you want to delete? You cannot restore this once it
              has been deleted.
            </span>
          </Label>
        </div>
        <Modal.Footer>
          <Button
            className="w-full sm:w-auto"
            layout="squared"
            variant="white"
            font="boldGray"
            onClick={() => setDeleteModal(null)}
          >
            Cancel
          </Button>
          <Button
            className="ml-3 w-full sm:w-auto"
            layout="squared"
            variant="primary"
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <LoadingSpinner color="text-primary" />
            ) : (
              <span>Delete</span>
            )}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
